import type { Entry, Tag, Track, TrackValue, ValidationError } from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '../../components/Button.js'
import { Chip } from '../../components/Chip.js'
import { useToast } from '../../components/Toast.js'
import { ValueInput } from '../../components/value-input/ValueInput.js'
import { formatDateTime } from '../../format/dates.js'
import { useDeclareUnsaved } from '../../pwa/UnsavedWork.js'
import { useServices } from '../../services/ServicesProvider.js'
import formStyles from '../log/EntryForm.module.css'
import styles from './Timeline.module.css'

/**
 * One entry, editable (T-306).
 *
 * Corrections are routine: a value entered in haste, a missing note, a
 * timestamp that was actually forty minutes earlier. Without editing, people
 * either abandon flawed entries or stop trusting the record — and a record
 * nobody trusts is not evidence.
 */
export function EntryDetailScreen() {
  const { repos, clock } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()
  const entryId = useParams()['id'] ?? ''

  const [entry, setEntry] = useState<Entry | null>(null)
  const [tracks, setTracks] = useState<Track[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [values, setValues] = useState<Record<string, TrackValue>>({})
  const [tagIds, setTagIds] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [at, setAt] = useState<Entry['at'] | null>(null)
  const [errors, setErrors] = useState<readonly ValidationError[]>([])
  const [dirty, setDirty] = useState(false)
  const [notFound, setNotFound] = useState(false)

  useDeclareUnsaved(dirty)

  const load = useCallback(async () => {
    const [found, allTracks, allTags] = await Promise.all([
      repos.entries.get(entryId),
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
    ])
    setTracks(allTracks)
    setTags(allTags)
    if (!found) {
      setNotFound(true)
      return
    }
    setEntry(found)
    setValues(Object.fromEntries(found.measurements.map((m) => [m.trackId, m.value])))
    setTagIds([...found.tagIds])
    setNote(found.note ?? '')
    setAt(found.at)
  }, [entryId, repos])

  useEffect(() => {
    void load()
  }, [load])

  async function save() {
    if (!entry || !at) {
      return
    }
    setErrors([])
    const measurements = Object.entries(values).map(([trackId, value]) => ({
      trackId,
      value,
      ...(entry.measurements.find((m) => m.trackId === trackId)?.note === undefined
        ? {}
        : { note: entry.measurements.find((m) => m.trackId === trackId)!.note! }),
    }))

    const result = await repos.entries.update(entry.id, {
      at,
      tagIds,
      measurements,
      ...(note.trim() === '' ? { note: undefined } : { note: note.trim() }),
    })

    if (!result.ok) {
      setErrors(result.errors)
      return
    }
    setDirty(false)
    show({ message: 'Saved.' })
    void navigate('/timeline')
  }

  async function remove() {
    if (!entry) {
      return
    }
    const result = await repos.entries.remove(entry.id)
    if (!result.ok) {
      show({ message: 'Could not delete that.', tone: 'danger' })
      return
    }
    const removed = result.value
    void navigate('/timeline')
    /*
     * Undo, not a confirmation dialog.
     *
     * Confirmations get dismissed reflexively — the tap is muscle memory
     * before the sentence is read. Undo catches the mistake once it has been
     * *noticed*, which is when people actually notice. `restore` puts the entry
     * back with its original id, so anything holding a reference still points
     * at something that exists (T-107).
     */
    show({
      message: 'Entry deleted.',
      tone: 'danger',
      action: {
        label: 'Undo',
        onAction: () => {
          void repos.entries.restore(removed).then(() => {
            show({ message: 'Entry restored.' })
          })
        },
      },
    })
  }

  if (notFound) {
    return (
      <>
        <h1>No such entry</h1>
        <p>It may have been deleted.</p>
        <Button onClick={() => void navigate('/timeline')}>Back to the timeline</Button>
      </>
    )
  }

  if (!entry || !at) {
    return (
      <>
        <h1>Entry</h1>
        <p>Loading…</p>
      </>
    )
  }

  const measured = tracks.filter((track) => values[track.id] !== undefined)
  const addable = tracks.filter(
    (track) => !track.archived && values[track.id] === undefined,
  )
  const elsewhere = at.timeZone !== clock.timeZone()

  return (
    <>
      <h1>Entry</h1>

      <div className={formStyles['form']}>
        <div className={formStyles['when']}>
          <div className={formStyles['whenRow']}>
            <span className={formStyles['whenValue']} data-testid="entry-when">
              {formatDateTime(at)}
            </span>
            {elsewhere && <span className={styles['entryZone']}>{at.timeZone}</span>}
          </div>
          <input
            type="datetime-local"
            className={formStyles['datetime']}
            aria-label="Date and time"
            value={toLocalInput(at)}
            onChange={(event) => {
              const parsed = Date.parse(event.target.value)
              if (Number.isFinite(parsed)) {
                // Zone preserved: correcting the time does not relocate the
                // entry (ADR-0006).
                setAt({ epochMs: parsed, timeZone: at.timeZone })
                setDirty(true)
              }
            }}
          />
        </div>

        {measured.map((track) => (
          <section key={track.id} className={formStyles['slot']}>
            <div className={formStyles['slotHeader']}>
              <span className={formStyles['slotName']} id={`label-${track.id}`}>
                {track.name}
                {track.archived ? ' (archived)' : ''}
              </span>
              {track.unit !== undefined && (
                <span className={formStyles['slotUnit']}>{track.unit}</span>
              )}
            </div>
            <ValueInput
              track={track}
              value={values[track.id] ?? null}
              labelledBy={`label-${track.id}`}
              onChange={(next) => {
                setValues((current) => {
                  const updated = { ...current }
                  if (next === null) {
                    delete updated[track.id]
                  } else {
                    updated[track.id] = next
                  }
                  return updated
                })
                setDirty(true)
              }}
            />
            <button
              type="button"
              className={formStyles['skip']}
              onClick={() => {
                setValues((current) => {
                  const updated = { ...current }
                  delete updated[track.id]
                  return updated
                })
                setDirty(true)
              }}
            >
              Remove {track.name} from this entry
            </button>
          </section>
        ))}

        {addable.length > 0 && (
          <section className={formStyles['section']}>
            <h2 className={formStyles['sectionTitle']}>Add another measurement</h2>
            <div className={formStyles['tags']}>
              {addable.map((track) => (
                <Chip
                  key={track.id}
                  onClick={() => {
                    // Seeded from the track's own minimum where it has one, so
                    // the control opens somewhere valid rather than empty.
                    const seed: TrackValue =
                      track.kind.kind === 'numeric'
                        ? track.kind.min
                        : track.kind.kind === 'boolean'
                          ? true
                          : (track.kind.options[0]?.id ?? '')
                    setValues((current) => ({ ...current, [track.id]: seed }))
                    setDirty(true)
                  }}
                >
                  + {track.name}
                </Chip>
              ))}
            </div>
          </section>
        )}

        <section className={formStyles['section']}>
          <h2 className={formStyles['sectionTitle']}>Tags</h2>
          <div className={formStyles['tags']}>
            {tags.map((tag) => (
              <Chip
                key={tag.id}
                {...(tag.colour === undefined ? {} : { colour: tag.colour })}
                selected={tagIds.includes(tag.id)}
                onClick={() => {
                  setTagIds(
                    tagIds.includes(tag.id)
                      ? tagIds.filter((id) => id !== tag.id)
                      : [...tagIds, tag.id],
                  )
                  setDirty(true)
                }}
              >
                {tag.name}
              </Chip>
            ))}
          </div>
        </section>

        <section className={formStyles['section']}>
          <h2 className={formStyles['sectionTitle']}>Note</h2>
          <textarea
            className={formStyles['note']}
            aria-label="Note about this entry"
            value={note}
            onChange={(event) => {
              setNote(event.target.value)
              setDirty(true)
            }}
          />
        </section>

        {errors.length > 0 && (
          <div className={formStyles['errors']} role="alert">
            {errors.map((error) => (
              <span key={error.code} className={formStyles['error']}>
                {error.message}
              </span>
            ))}
          </div>
        )}

        <div className={formStyles['actions']}>
          <Button variant="danger" onClick={() => void remove()}>
            Delete
          </Button>
          <Button variant="primary" fullWidth onClick={() => void save()}>
            Save changes
          </Button>
        </div>
      </div>
    </>
  )
}

/** `<input type="datetime-local">` wants wall-clock text with no zone. */
function toLocalInput(at: Entry['at']): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: at.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at.epochMs)
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}
